import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "../../lib/api";
import { ProviderLogo } from "../shared/ProviderLogo";
import { DialogPanel } from "../shared/DialogPanel";
import { PROVIDER_REASONING_LEVELS, reasoningLabel } from "../../lib/reasoning";
import { ModelSelector, type ModelSelectorValue } from "../agent/ModelSelector";
import { backdropClose } from "../shared/backdrop";

interface AvailableModel {
  id: string;
  provider: string;
  model_name: string;
  label: string;
  thinking_levels: string[];
  price_in_usd: number;
  price_out_usd: number;
  price_cached_usd: number;
  agent_count: number;
}

const PROVIDERS = ["openai", "anthropic", "google", "grok", "openrouter", "deepseek", "meta", "groq"];
const PROVIDER_LABEL: Record<string, string> = {
  openai: "OpenAI", anthropic: "Anthropic", google: "Google", grok: "Grok", openrouter: "OpenRouter", deepseek: "DeepSeek", meta: "Meta", groq: "Groq (Whisper)",
};

const inp: React.CSSProperties = {
  padding: "8px 12px",
  background: "var(--surface-input)",
  border: "1px solid var(--line-strong)",
  borderRadius: 8,
  color: "var(--ink)",
  fontSize: 13,
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

const lbl: React.CSSProperties = { fontSize: 12, color: "var(--ink-soft)", display: "block", marginBottom: 5 };

const BLANK: AvailableModel = { id: "", provider: "openai", model_name: "", label: "", thinking_levels: [], price_in_usd: 0, price_out_usd: 0, price_cached_usd: 0, agent_count: 0 };

// Aceita "1,50", "1,5", "1.5", "1.50" → 1.5. Vazio/invalido → 0.
function parsePrice(s: string): number {
  const n = parseFloat(s.trim().replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

export function AvailableModelsTab() {
  const qc = useQueryClient();
  const { data: models = [] } = useQuery<AvailableModel[]>({
    queryKey: ["dev-available-models"],
    queryFn: () => api.get("/dev/available-models"),
  });
  const [editing, setEditing] = useState<AvailableModel | null>(null);
  const [deleting, setDeleting] = useState<AvailableModel | null>(null);

  const del = useMutation({
    mutationFn: ({ id, replacement }: { id: string; replacement?: ModelSelectorValue }) =>
      api.delete(`/dev/available-models/${id}`, replacement),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dev-available-models"] });
      qc.invalidateQueries({ queryKey: ["available-models"] });
      setDeleting(null);
    },
  });

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>Modelos Disponíveis</span>
        <button
          onClick={() => setEditing(BLANK)}
          style={{ padding: "7px 14px", background: "var(--brand)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, cursor: "pointer" }}
        >
          + Novo modelo
        </button>
      </div>

      {models.length === 0 ? (
        <div style={{ color: "var(--ink-muted)", fontSize: 13, textAlign: "center", padding: "40px 0" }}>Nenhum modelo cadastrado</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {models.map((m) => (
            <div key={m.id} style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 10, padding: "14px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer", flex: 1 }} onClick={() => setEditing(m)}>
                <ProviderLogo provider={m.provider} size={26} />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: "var(--ink)" }}>{m.label}</div>
                  <div style={{ fontSize: 12, color: "var(--ink-muted)", marginTop: 3, fontFamily: "var(--font-mono)" }}>{m.model_name}</div>
                  <div style={{ fontSize: 11, color: "var(--ink-muted)", marginTop: 4 }}>in ${m.price_in_usd}/1M · cache ${m.price_cached_usd}/1M · out ${m.price_out_usd}/1M</div>
                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 6 }}>
                    {(m.thinking_levels ?? []).map((t) => (
                      <span key={t} style={{ fontSize: 11, padding: "1px 7px", borderRadius: 99, background: "var(--surface-input)", color: "var(--ink-secondary)", border: "1px solid var(--line-strong)" }}>{reasoningLabel(t)}</span>
                    ))}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setDeleting(m)}
                style={{ background: "none", border: "none", color: "var(--danger)", fontSize: 12, cursor: "pointer", padding: "4px 8px" }}
              >
                Excluir
              </button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ModelModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ["dev-available-models"] }); setEditing(null); }}
        />
      )}

      {deleting && (
        <DeleteModelDialog
          model={deleting}
          models={models}
          pending={del.isPending}
          onClose={() => setDeleting(null)}
          onConfirm={(replacement) => del.mutate({ id: deleting.id, replacement })}
        />
      )}
    </div>
  );
}

const dlgOverlay: React.CSSProperties = { position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any };
const dlgBox: React.CSSProperties = { background: "var(--surface-app)", border: "1px solid var(--line)", borderRadius: 12, padding: 24, width: 540, display: "flex", flexDirection: "column", gap: 16 };
const dlgGhost: React.CSSProperties = { padding: "8px 16px", background: "var(--surface-input)", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 13, cursor: "pointer" };
const dlgRed: React.CSSProperties = { padding: "8px 16px", background: "var(--brand)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, cursor: "pointer" };

function DeleteModelDialog({ model, models, pending, onClose, onConfirm }: {
  model: AvailableModel;
  models: AvailableModel[];
  pending: boolean;
  onClose: () => void;
  onConfirm: (replacement?: ModelSelectorValue) => void;
}) {
  const count = model.agent_count;
  const others = models.filter((m) => m.id !== model.id);
  const first = others[0];
  const [step, setStep] = useState<1 | 2>(1);
  const [repl, setRepl] = useState<ModelSelectorValue>({
    provider: first?.provider ?? "openai",
    model_name: first?.model_name ?? "",
    temperature: 0.7,
    reasoning_effort: first?.thinking_levels?.includes("off") ? "off" : (first?.thinking_levels?.[0] ?? "off"),
  });

  // Sem agentes usando → confirma e exclui direto.
  if (count === 0) {
    return (
      <div {...backdropClose(() => { onClose(); })} style={dlgOverlay}>
        <DialogPanel onClose={() => onClose()} role="alertdialog" label={`Excluir ${model.label}`} style={dlgBox}>
          <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>Excluir “{model.label}”?</span>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button onClick={onClose} style={dlgGhost}>Cancelar</button>
            <button onClick={() => onConfirm()} disabled={pending} style={dlgRed}>{pending ? "Excluindo..." : "Excluir"}</button>
          </div>
        </DialogPanel>
      </div>
    );
  }

  return (
    <div {...backdropClose(() => { onClose(); })} style={dlgOverlay}>
      <DialogPanel onClose={() => onClose()} role="alertdialog" label={`Excluir ${model.label}`} style={dlgBox}>
        {step === 1 ? (
          <>
            <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>Excluir “{model.label}”?</span>
            <span style={{ fontSize: 13, color: "var(--ink-secondary)" }}>
              {count} agente{count > 1 ? "s" : ""} {count > 1 ? "usam" : "usa"} este modelo. Deseja excluir mesmo assim?
            </span>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={onClose} style={dlgGhost}>Cancelar</button>
              <button onClick={() => setStep(2)} style={dlgRed}>Continuar</button>
            </div>
          </>
        ) : (
          <>
            <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>
              Escolha um modelo para substituir os {count} agente{count > 1 ? "s" : ""}
            </span>
            {others.length === 0 ? (
              <span style={{ fontSize: 13, color: "var(--danger)" }}>Cadastre outro modelo antes de excluir este.</span>
            ) : (
              <ModelSelector value={repl} onChange={setRepl} showMaxTokens={false} compact />
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setStep(1)} style={dlgGhost}>Voltar</button>
              <button
                onClick={() => onConfirm(repl)}
                disabled={pending || others.length === 0 || !repl.model_name}
                style={dlgRed}
              >
                {pending ? "Aplicando..." : "Excluir e substituir"}
              </button>
            </div>
          </>
        )}
      </DialogPanel>
    </div>
  );
}

function ModelModal({ initial, onClose, onSaved }: { initial: AvailableModel; onClose: () => void; onSaved: () => void }) {
  const isEdit = !!initial.id;
  const [provider, setProvider] = useState(initial.provider || "openai");
  const [modelName, setModelName] = useState(initial.model_name);
  const [label, setLabel] = useState(initial.label);
  const [levels, setLevels] = useState<string[]>(initial.thinking_levels ?? []);
  const [priceIn, setPriceIn] = useState(initial.price_in_usd ? String(initial.price_in_usd) : "");
  const [priceOut, setPriceOut] = useState(initial.price_out_usd ? String(initial.price_out_usd) : "");
  const [priceCached, setPriceCached] = useState(initial.price_cached_usd ? String(initial.price_cached_usd) : "");
  const [error, setError] = useState("");

  const mut = useMutation({
    mutationFn: () => {
      const body = { provider, model_name: modelName.trim(), label: label.trim(), thinking_levels: levels, price_in_usd: parsePrice(priceIn), price_out_usd: parsePrice(priceOut), price_cached_usd: parsePrice(priceCached) };
      return isEdit ? api.patch(`/dev/available-models/${initial.id}`, body) : api.post("/dev/available-models", body);
    },
    onSuccess: onSaved,
    onError: (e: any) => setError(e?.payload?.detail ?? e.message ?? "Erro"),
  });

  function changeProvider(p: string) {
    setProvider(p);
    setLevels((ls) => ls.filter((t) => (PROVIDER_REASONING_LEVELS[p] ?? []).includes(t)));
  }
  function toggleLevel(t: string) {
    setLevels((ls) => (ls.includes(t) ? ls.filter((x) => x !== t) : [...ls, t]));
  }

  const possible = PROVIDER_REASONING_LEVELS[provider] ?? [];

  return (
    <div {...backdropClose(() => { onClose(); })} style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}>
      <DialogPanel onClose={() => onClose()} label={isEdit ? "Editar modelo" : "Novo modelo"} style={{ background: "var(--surface-app)", border: "1px solid var(--line)", borderRadius: 12, padding: 24, width: 520, display: "flex", flexDirection: "column", gap: 14 }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>{isEdit ? "Editar modelo" : "Novo modelo"}</span>

        <div>
          <label style={lbl}>Provedor</label>
          <select style={inp} value={provider} onChange={(e) => changeProvider(e.target.value)}>
            {PROVIDERS.map((p) => <option key={p} value={p}>{PROVIDER_LABEL[p]}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Nome técnico do modelo</label>
          <input style={inp} placeholder="ex: gemini-3-pro" value={modelName} onChange={(e) => setModelName(e.target.value)} spellCheck={false} />
        </div>
        <div>
          <label style={lbl}>Rótulo (nome amigável)</label>
          <input style={inp} placeholder="ex: Gemini 3 Pro" value={label} onChange={(e) => setLabel(e.target.value)} />
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={lbl}>Preço input (US$/1M tokens)</label>
            <input style={inp} type="text" inputMode="decimal" placeholder="ex: 1,50" value={priceIn}
              onChange={(e) => setPriceIn(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={lbl}>Preço input cached (US$/1M)</label>
            <input style={inp} type="text" inputMode="decimal" placeholder="ex: 0,15" value={priceCached}
              onChange={(e) => setPriceCached(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={lbl}>Preço output (US$/1M tokens)</label>
            <input style={inp} type="text" inputMode="decimal" placeholder="ex: 1,50" value={priceOut}
              onChange={(e) => setPriceOut(e.target.value)} />
          </div>
        </div>
        <div>
          <label style={lbl}>Esforços de raciocínio disponíveis</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 2 }}>
            {possible.map((t) => {
              const on = levels.includes(t);
              return (
                <button key={t} type="button" onClick={() => toggleLevel(t)}
                  style={{
                    padding: "5px 11px", borderRadius: 8, fontSize: 12, cursor: "pointer",
                    background: on ? "var(--brand-soft)" : "var(--surface-input)",
                    border: `1px solid ${on ? "var(--brand)" : "var(--line-strong)"}`,
                    color: on ? "var(--brand)" : "var(--ink-secondary)",
                  }}>
                  {reasoningLabel(t)}
                </button>
              );
            })}
          </div>
        </div>

        {error && <div style={{ fontSize: 12, color: "var(--danger)" }}>{error}</div>}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "8px 16px", background: "var(--surface-input)", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 13, cursor: "pointer" }}>Cancelar</button>
          <button onClick={() => mut.mutate()} disabled={mut.isPending || !modelName.trim() || !label.trim()} style={{ padding: "8px 16px", background: "var(--brand)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, cursor: "pointer" }}>
            {mut.isPending ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </DialogPanel>
    </div>
  );
}
